import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Input, Select, Button, Alert, message } from 'antd';
import {
    FolderAddOutlined,
    ArrowLeftOutlined,
    CheckCircleOutlined,
    InfoCircleOutlined
} from '@ant-design/icons';
import apiService from '../../services/api';
import { ServiceType } from '../../services/api';
import {
    Container,
    PageHeader,
    PageTitle,
    PageSubtitle,
    FormContainer,
    FormCard,
    FormSection,
    SectionTitle,
    SectionDescription,
    FormGrid,
    FormItem,
    SubmitButton,
    CancelButton,
    SuccessCard,
    IconWrapper,
    ButtonGroup,
    InfoBox,
    StepIndicator,
    StepItem,
    StepNumber,
    StepLabel
} from './styles';

const { TextArea } = Input;

interface Broker {
    id: string;
    name: string;
    code: string;
}

const coreService = apiService.getService(ServiceType.CORE);

const RegisterPortfolio: React.FC = () => {
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [brokers, setBrokers] = useState<Broker[]>([]);
    const [loadingBrokers, setLoadingBrokers] = useState(true);

    // Fetch available brokers on component mount
    useEffect(() => {
        const fetchBrokers = async () => {
            try {
                const token = localStorage.getItem('access_token');
                if (!token) {
                    message.error('Please login first');
                    navigate('/signin');
                    return;
                }

                const response = await coreService.get('/brokers', {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });

                setBrokers(response.data);
                setLoadingBrokers(false);
            } catch (error) {
                console.error('Error fetching brokers:', error);
                message.error('Failed to load brokers');
                setLoadingBrokers(false);
            }
        };

        fetchBrokers();
    }, [navigate]);

    const handleSubmit = async (values: any) => {
        setLoading(true);

        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                message.error('Please login first');
                navigate('/signin');
                return;
            }

            // TODO: Replace with actual API endpoint
            const response = await coreService.post('/registered-portfolios', values, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            setLoading(false);
            setSuccess(true);

            // Redirect to pair trading page after 2 seconds
            setTimeout(() => {
                navigate('/pair-trading');
            }, 2000);
        } catch (error) {
            console.error('Error registering portfolio:', error);
            message.error('Failed to register portfolio');
            setLoading(false);
        }
    };

    const handleCancel = () => {
        navigate(-1);
    };

    if (success) {
        return (
            <Container>
                <SuccessCard>
                    <IconWrapper>
                        <CheckCircleOutlined style={{ fontSize: '64px', color: '#0ECB81' }} />
                    </IconWrapper>
                    <PageTitle style={{ marginTop: '24px' }}>Portfolio Created Successfully!</PageTitle>
                    <PageSubtitle>
                        Your portfolio has been registered. Redirecting to Pair Trading...
                    </PageSubtitle>
                </SuccessCard>
            </Container>
        );
    }

    return (
        <Container>
            <PageHeader>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
                    <Button
                        icon={<ArrowLeftOutlined />}
                        onClick={handleCancel}
                        style={{
                            background: 'transparent',
                            border: '1px solid #2B3139',
                            color: '#EAECEF'
                        }}
                    >
                        Back
                    </Button>
                    <div>
                        <PageTitle>Register New Portfolio</PageTitle>
                        <PageSubtitle>
                            Create a portfolio to start pair trading with futures and stocks
                        </PageSubtitle>
                    </div>
                </div>
                <Alert
                    message="Welcome to Pair Trading!"
                    description="To get started with pair trading strategies, you'll need to create your first portfolio. This will help you organize your trades, track performance, and manage risk effectively."
                    type="info"
                    showIcon
                    closable
                    style={{
                        background: 'rgba(14, 203, 129, 0.1)',
                        border: '1px solid rgba(14, 203, 129, 0.3)',
                        color: '#EAECEF'
                    }}
                />
            </PageHeader>

            <FormContainer>
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    initialValues={{
                        status: 'active'
                    }}
                >
                    <FormCard>
                        <FormSection>
                            <SectionTitle>
                                <FolderAddOutlined style={{ color: '#0ECB81', marginRight: '8px' }} />
                                Basic Information
                            </SectionTitle>
                            <SectionDescription>
                                Provide basic details about your trading portfolio
                            </SectionDescription>

                            <FormGrid>
                                <FormItem
                                    name="portfolioId"
                                    label="Portfolio ID"
                                    rules={[
                                        { required: true, message: 'Please enter portfolio ID' },
                                        { min: 3, message: 'Portfolio ID must be at least 3 characters' }
                                    ]}
                                >
                                    <Input
                                        size="large"
                                        placeholder="e.g., 05C000089"
                                        prefix={<FolderAddOutlined style={{ color: '#848E9C' }} />}
                                    />
                                </FormItem>

                                <FormItem
                                    name="portfolioName"
                                    label="Portfolio Name"
                                    rules={[
                                        { required: true, message: 'Please enter portfolio name' },
                                        { min: 3, message: 'Portfolio name must be at least 3 characters' }
                                    ]}
                                >
                                    <Input
                                        size="large"
                                        placeholder="e.g., My Trading Portfolio"
                                        prefix={<FolderAddOutlined style={{ color: '#848E9C' }} />}
                                    />
                                </FormItem>
                            </FormGrid>
                        </FormSection>

                        <FormSection>
                            <SectionTitle>
                                <InfoCircleOutlined style={{ color: '#0ECB81', marginRight: '8px' }} />
                                Broker Configuration
                            </SectionTitle>
                            <SectionDescription>
                                Connect your broker account to enable automated trading
                            </SectionDescription>


                            <FormGrid>
                                <FormItem
                                    name="brokerCode"
                                    label="Select Broker"
                                    rules={[{ required: true, message: 'Please select a broker' }]}
                                >
                                    <Select
                                        size="large"
                                        placeholder="Choose your broker"
                                        loading={loadingBrokers}
                                        disabled={loadingBrokers}
                                    >
                                        {brokers.map(broker => (
                                            <Select.Option key={broker.id} value={broker.code}>
                                                {broker.name} ({broker.code})
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </FormItem>
                            </FormGrid>


                        </FormSection>

                        <Alert
                            message="Important Notice"
                            description="By creating this portfolio, you agree to our trading terms and conditions. You can modify these settings at any time from the portfolio management page."
                            type="info"
                            showIcon
                            style={{
                                background: 'rgba(14, 203, 129, 0.1)',
                                border: '1px solid rgba(14, 203, 129, 0.3)',
                                color: '#EAECEF',
                                marginBottom: '24px'
                            }}
                        />

                        <ButtonGroup>
                            <CancelButton onClick={handleCancel} disabled={loading}>
                                Cancel
                            </CancelButton>
                            <SubmitButton
                                type="primary"
                                htmlType="submit"
                                icon={<FolderAddOutlined />}
                                loading={loading}
                                size="large"
                            >
                                Create Portfolio
                            </SubmitButton>
                        </ButtonGroup>
                    </FormCard>
                </Form>
            </FormContainer>
        </Container>
    );
};

export default RegisterPortfolio;

